import React, { useState, useEffect } from "react";
import StrategyTableHeader from "./StrategyTableHeader";
import StrategyTableRow from "./StrategyTableRow";
import LoadingSkeleton from "./LoadingSkeleton";
import PaginationControls from "../../shared/PaginationControls";
import ItemsPerPageDropdown from "../../shared/ItemsPerPageDropdown";
import { useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { getStrategyRecords, SortStrategyBySymbol } from "../../../api/parametersApi";
import usePageTitle from "../../../hooks/usePageTitle";
import { getAllSymbols } from "../../../api/orderApi";
import DropdownSort from "../../shared/DropDownSort";

const StrategyRecord = ({ accountType }) => {
   usePageTitle("Strategies");
   const [selectedRows, setSelectedRows] = useState([]);
   const [selectAll, setSelectAll] = useState(false);
   const [currentPage, setCurrentPage] = useState(1);
   const [isLoading, setIsLoading] = useState(true);
   const [itemsPerPage, setItemsPerPage] = useState(50);
   const [records, setRecords] = useState([]);
   const [totalItems, setTotalItems] = useState(0);
   const [symbolOptions, setSymbolOptions] = useState([]);
   const [selectedSymbol, setSelectedSymbol] = useState("");
   const [sortTypeSymbol, setSortTypeSymbol] = useState(0);

   const [searchParams, setSearchParams] = useSearchParams();

   useEffect(() => {
      const pageIndex = parseInt(searchParams.get("pageIndex"), 10) || 1;
      const symbol = searchParams.get("symbol");

      setCurrentPage(pageIndex);

      if (symbolOptions.length === 0) {
         return;
      }

      if (symbol) {
         const foundSymbol = symbolOptions.find(
            (option) => option.value === symbol
         );
         setSelectedSymbol(
            foundSymbol
               ? foundSymbol
               : { value: foundSymbol.value, label: foundSymbol.label }
         );
      } else {
         setSelectedSymbol("");
      }
   }, [searchParams, symbolOptions]);

   const handleSortBySymbol = () => {
      setSortTypeSymbol((prevSortType) => (prevSortType + 1) % 3);
      setCurrentPage(1);
      console.log(sortTypeSymbol)
    };

   useEffect(() => {
      const fetchData = async () => {
         const token = Cookies.get("accessToken");
         if (!token) {
            return { account: null, statusCode: 401 };
         }
         setIsLoading(true);
         try {
            let data;
            if (sortTypeSymbol === 1 || sortTypeSymbol === 2) {
               data = await SortStrategyBySymbol(
                  currentPage - 1,
                  itemsPerPage,
                  sortTypeSymbol,
                  token
               );
            } else {
               const filters = [
                  {
                     name: "id",
                     value: "",
                  },
               ];
               if (selectedSymbol) {
                  filters.push({
                     name: "symbol",
                     value: selectedSymbol.value === "all" ? "" : selectedSymbol.value,
                  });
               }
               data = await getStrategyRecords(token, currentPage - 1, itemsPerPage, filters);
            }
            if (data) {
               setRecords(data.data);
               setTotalItems(data.paging?.totalCount);
            } else {
               console.error("Unexpected data format:", data);
            }
         } catch (error) {
            console.error("Error fetching data:", error);
         } finally {
            setIsLoading(false);
         }
      };

      fetchData();
   }, [currentPage, itemsPerPage, selectedSymbol, sortTypeSymbol]);

   useEffect(() => {
      const fetchSymbols = async () => {
         const token = Cookies.get("accessToken");
         try {
            const result = await getAllSymbols(token);
            if (result && result.successful) {
               const symbols = result.data.symbols;
               setSymbolOptions([
                  { value: "all", label: "All Symbols" },
                  ...symbols.map((name) => ({ value: name, label: name })),
               ]);
            } else {
               console.error("Error fetching symbols:", result.error.message);
            }
         } catch (error) {
            console.error("Error fetching symbols:", error);
         }
      };

      fetchSymbols();
   }, []);

   const totalPages = Math.ceil(totalItems / itemsPerPage);

   const currentData = records;

   const hasRecords = currentData?.length > 0;

   useEffect(() => {
      setTimeout(() => {
         setIsLoading(false);
      }, 2000);
   }, []);

   const handleRowSelect = (index) => {
      const pageIndex = (currentPage - 1) * itemsPerPage + index;
      if (selectedRows.includes(pageIndex)) {
         setSelectedRows(selectedRows.filter((i) => i !== pageIndex));
      } else {
         setSelectedRows([...selectedRows, pageIndex]);
      }
   };

   const handleSelectAll = () => {
      if (selectAll) {
         setSelectedRows([]);
         setSelectAll(false);
      } else {
         const allIndexes = currentData.map(
            (_, index) => (currentPage - 1) * itemsPerPage + index
         );
         setSelectedRows(allIndexes);
         setSelectAll(true);
      }
   };

   const handlePageChange = (newPage) => {
      setCurrentPage(newPage);
      setSearchParams({
        pageIndex: newPage,
        symbol: selectedSymbol?.value || '',
      });
      setSelectAll(false);
   };

   const handleSymbolChange = (selectedOption) => {
      setSelectedSymbol(selectedOption);
      setSearchParams({
         pageIndex: 1,
         symbol: selectedOption?.value || "",
      });
      setCurrentPage(1);
   };

   const handleItemsPerPageChange = (event) => {
      setItemsPerPage(Number(event.target.value));
      setCurrentPage(1);
   };

   const handleResetFilters = () => {
		setSelectedSymbol(null);
		setSearchParams({});
      setSortTypeSymbol(0);
		setCurrentPage(1);
	};

   return (
      <div className="bg-grey-light md:p-16 p-4 min-h-screen">
         <h1 className="text-purple-circle font-bold md:text-3xl text-base text-center md:text-left md:ml-20 md:mb-10 mb-5">
            Strategy Setting Record
         </h1>
         <div className="flex justify-end items-center py-2 md:px-20 px-5 md:space-x-5 space-x-1">
            <span className="font-semibold md:text-sm text-[6px]">
               Total({totalItems})
            </span>
            <div className="xl:w-1/7">
               <DropdownSort
                  options={symbolOptions}
                  selected={selectedSymbol}
                  onChange={handleSymbolChange}
                  title={"Symbol"}
               />
            </div>
            <button onClick={handleResetFilters}>
               <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="md:size-6 size-3"
               >
                  <path
                     strokeLinecap="round"
                     strokeLinejoin="round"
                     d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z"
                  />
               </svg>
            </button>
         </div>
         <div className="flex flex-col justify-center items-center gap-8">
            <div className="w-full flex-col items-start bg-[#ffffff] md:py-10 py-2 rounded-lg md:px-12 px-1.5">
               <StrategyTableHeader
                  selectAll={selectAll}
                  handleSelectAll={handleSelectAll}
                  sortBySymbol={handleSortBySymbol}
               />

               {isLoading ? (
                  <LoadingSkeleton itemsPerPage={itemsPerPage} />
               ) : hasRecords ? (
                  currentData.map((item, index) => (
                     <StrategyTableRow
                        key={index}
                        item={item}
                        index={(currentPage - 1) * itemsPerPage + index}
                        isSelected={selectedRows.includes(
                           (currentPage - 1) * itemsPerPage + index
                        )}
                        handleRowSelect={handleRowSelect}
                        accountType={accountType}
                     />
                  ))
               ) : (
                  <div className="text-center pt-10">No records available</div>
               )}

               <div className="flex justify-end space-x-5">
                  <ItemsPerPageDropdown
                     itemsPerPage={itemsPerPage}
                     handleItemsPerPageChange={handleItemsPerPageChange}
                     totalItems={totalItems}
                  />
                  <PaginationControls
                     currentPage={currentPage}
                     totalPages={totalPages}
                     handlePageChange={handlePageChange}
                     itemsPerPage={itemsPerPage}
                     totalItems={totalItems}
                  />
               </div>
            </div>
         </div>
      </div>
   );
};

export default StrategyRecord;
